import { useState } from "react";
import { useAuth } from "@/auth-provider";
import { useNavigate } from "react-router-dom";
import { MapPin, Settings, Check, Sun, Moon, Monitor, Palette as PaletteIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { GlassPanel } from "@/components/ui/glass-panel";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useUnit } from "@/components/unit-provider";
import { useTheme } from "next-themes";
import { useLocation } from "@/components/location-provider";
import { PageHeader } from "@/components/page-header";
import { SavedLocations } from "@/components/saved-locations";
import { usePalette, PALETTES } from "@/components/palette-provider";
import { cn } from "@/lib/utils";

export default function AccountPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { unit, setUnit } = useUnit();
  const { theme, setTheme } = useTheme();
  const { palette, setPalette } = usePalette();
  const [mapLayer, setMapLayer] = useState("temp_new");
  const [saved, setSaved] = useState(false);
  const { current: location } = useLocation();

  const handleSave = () => {
    localStorage.setItem("vertali_prefs", JSON.stringify({ mapLayer }));
    if (user?.id) {
      fetch("/api/preferences", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mapLayer }),
      }).catch(() => {});
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleSignOut = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="space-y-5 animate-slide-up">
      <PageHeader
        eyebrow="Settings"
        title="Account"
        subtitle="Manage your preferences"
      />

      <GlassPanel intensity="low" className="p-5">
        <div className="flex items-center gap-4">
          <Avatar className="h-14 w-14">
            <AvatarFallback className="text-sm">
              {(user?.name || "V").slice(0, 1).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-lg">{user?.name || "Vertali User"}</h3>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
            <div className="flex items-center gap-2 mt-0.5">
              <MapPin className="h-3 w-3 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{location.name}, {location.country}</span>
            </div>
            <Badge variant={user ? "success" : "outline"} size="sm" className="mt-1.5">
              {user ? "Verified" : "Local mode"}
            </Badge>
          </div>
          {user ? (
            <Button variant="outline" size="sm" onClick={handleSignOut}>
              Sign Out
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={() => navigate("/login")}>
              Sign In
            </Button>
          )}
        </div>
      </GlassPanel>

      <div className="grid gap-4 md:grid-cols-2">
        <GlassPanel intensity="low" className="p-5">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <Settings className="h-4 w-4 text-primary" />
            </div>
            <h3 className="text-sm font-semibold">Temperature Display</h3>
          </div>
          <div className="space-y-3">
            <label className="text-sm font-medium">Unit Preference</label>
            <div className="flex gap-2">
              <Button variant={unit === "C" ? "default" : "outline"} size="sm" onClick={() => setUnit("C")}>°C Only</Button>
              <Button variant={unit === "CF" ? "default" : "outline"} size="sm" onClick={() => setUnit("CF")}>°C / °F</Button>
              <Button variant={unit === "F" ? "default" : "outline"} size="sm" onClick={() => setUnit("F")}>°F Only</Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Currently: {unit === "CF" ? "Both" : unit === "F" ? "Fahrenheit" : "Celsius"}
            </p>
          </div>
        </GlassPanel>

        <GlassPanel intensity="low" className="p-5">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <PaletteIcon className="h-4 w-4 text-primary" />
            </div>
            <h3 className="text-sm font-semibold">Appearance</h3>
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Mode
              </label>
              <div className="mt-2 flex gap-2">
                <Button variant={theme === "light" ? "default" : "outline"} size="sm" onClick={() => setTheme("light")}>
                  <Sun className="h-3.5 w-3.5 mr-1.5" /> Light
                </Button>
                <Button variant={theme === "dark" ? "default" : "outline"} size="sm" onClick={() => setTheme("dark")}>
                  <Moon className="h-3.5 w-3.5 mr-1.5" /> Dark
                </Button>
                <Button variant={theme === "system" ? "default" : "outline"} size="sm" onClick={() => setTheme("system")}>
                  <Monitor className="h-3.5 w-3.5 mr-1.5" /> System
                </Button>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Palette
              </label>
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                {PALETTES.map((p) => {
                  const active = palette === p.id;
                  return (
                    <button
                      key={p.id}
                      onClick={() => setPalette(p.id)}
                      className={cn(
                        "rounded-xl border p-3 text-left transition-all",
                        active
                          ? "border-primary/60 bg-primary/10 shadow-sm shadow-primary/20"
                          : "border-border/40 bg-background/30 hover:border-border/70",
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-base">{p.emoji}</span>
                        {active && (
                          <Check className="h-3.5 w-3.5 text-primary" />
                        )}
                      </div>
                      <p className={cn("mt-1 text-sm font-bold", active && "text-primary")}>{p.label}</p>
                      <p className="text-[10px] text-muted-foreground leading-snug mt-0.5">
                        {p.description}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </GlassPanel>

        <GlassPanel intensity="low" className="p-5">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <MapPin className="h-4 w-4 text-primary" />
            </div>
            <h3 className="text-sm font-semibold">Map Preferences</h3>
          </div>
          <div className="space-y-3">
            <label className="text-sm font-medium">Default Map Layer</label>
            <select
              value={mapLayer}
              onChange={(e) => setMapLayer(e.target.value)}
              className="mt-1 block w-full rounded-lg border border-border/40 bg-background/50 backdrop-blur-sm px-3 py-2 text-sm outline-none focus:border-primary/50 transition-colors"
            >
              <option value="temp_new">Temperature</option>
              <option value="clouds_new">Clouds</option>
              <option value="precipitation_new">Precipitation</option>
              <option value="wind_new">Wind</option>
              <option value="pressure_new">Pressure</option>
            </select>
            <Button onClick={handleSave} className="w-full">
              {saved ? <><Check className="h-3.5 w-3.5 mr-1.5" />Saved</> : "Save Preferences"}
            </Button>
          </div>
        </GlassPanel>

        <SavedLocations />
      </div>
    </div>
  );
}